import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    UpdateChatSettings as UpdateChatSettingsMutation,
    UpdateChatSettingsVariables as UpdateChatSettingsMutationVariables,
    ChatSettingsLookAndFeelInput,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../../apollo-client';

import UPDATE_CHAT_SETTINGS from '../mutations/update-chat-settings.graphql';
import GET_APPEARANCE_SETTINGS from '../queries/get-appearance-settings.graphql';

export const useUpdateAppearanceSettings = (): {
    updateAppearanceSettings: (lookAndFeel: ChatSettingsLookAndFeelInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [updateChatSettings, {loading, error}] = useMutation<
        UpdateChatSettingsMutation,
        UpdateChatSettingsMutationVariables
    >(UPDATE_CHAT_SETTINGS);

    return {
        updateAppearanceSettings: (lookAndFeel: ChatSettingsLookAndFeelInput) => {
            return updateChatSettings({
                variables: {
                    input: {
                        chatSettings: {
                            lookAndFeel,
                        },
                    },
                },
                // @ts-expect-error
                optimisticResponse: () => {
                    const cacheResult = nutApolloClient.readQuery({
                        query: GET_APPEARANCE_SETTINGS,
                    });

                    const existingChatSettings = cacheResult && cacheResult.chatSettings;

                    if (!existingChatSettings) {
                        return {};
                    }

                    const existingLookAndFeel = existingChatSettings.lookAndFeel || {};

                    const optimisticLookAndFeel = {
                        __typename: 'LookAndFeel',
                        ...existingLookAndFeel,
                        ...lookAndFeel,
                    } as const;

                    // Preserve nested objects with proper merging
                    if (existingLookAndFeel.nudgeSettings || lookAndFeel.nudgeSettings) {
                        optimisticLookAndFeel.nudgeSettings = {
                            __typename: 'NudgeSettings',
                            ...existingLookAndFeel.nudgeSettings,
                            ...lookAndFeel.nudgeSettings,
                        };
                    }

                    if (existingLookAndFeel.icon) {
                        optimisticLookAndFeel.icon = {
                            __typename: 'Icon',
                            ...existingLookAndFeel.icon,
                        };
                    }

                    if (existingLookAndFeel.disclaimerSettings || lookAndFeel.disclaimerSettings) {
                        optimisticLookAndFeel.disclaimerSettings = {
                            __typename: 'DisclaimerSettings',
                            ...existingLookAndFeel.disclaimerSettings,
                            ...lookAndFeel.disclaimerSettings,
                        };
                    }

                    return {
                        chatSettingsUpdate: {
                            __typename: 'ChatSettings',
                            ...existingChatSettings,
                            lookAndFeel: optimisticLookAndFeel,
                        },
                    };
                },
                update: (proxy, mutationResults) => {
                    const newChatSettings =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.chatSettingsUpdate;

                    if (!newChatSettings) {
                        return;
                    }

                    const cacheResult = proxy.readQuery({
                        query: GET_APPEARANCE_SETTINGS,
                    });

                    if (cacheResult) {
                        proxy.writeQuery({
                            query: GET_APPEARANCE_SETTINGS,
                            data: {
                                ...cacheResult,
                                chatSettings: {
                                    // @ts-expect-error
                                    ...cacheResult.chatSettings,
                                    lookAndFeel: newChatSettings.lookAndFeel,
                                },
                            },
                        });
                    }
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
